/**
 * VoxelCollision: runtime sparse voxel octree collider.
 *
 * Ported from supersplat-viewer/src/collision/voxel-collision.ts.
 * Loads the two-file format (.voxel.json + .voxel.bin) produced by
 * splat-transform's writeVoxel and provides point/sphere/capsule/ray queries.
 */

import { PENETRATION_EPSILON, resolveIterative } from './collision';
import type { Collision, PushOut, RayHit } from './collision';

interface VoxelMetadata {
    version: string;
    gridBounds: { min: number[]; max: number[] };
    gaussianBounds: { min: number[]; max: number[] };
    voxelResolution: number;
    leafSize: number;
    treeDepth: number;
    numInteriorNodes: number;
    numMixedLeaves: number;
    nodeCount: number;
    leafDataCount: number;
}

/** Solid leaf node marker: childMask = 0xFF, baseOffset = 0. */
const SOLID_LEAF_MARKER = 0xFF000000 >>> 0;

/** Half-extent of the flatness sampling patch (5x5 when R=2). */
const FLAT_R = 2;

const INV_SQRT2 = 1 / Math.sqrt(2);

const SURFACE_CANDIDATES: number[][] = [
    [1, 0, 0, 0, 1, 0, 0, 0, 1],
    [0, 1, 0, 1, 0, 0, 0, 0, 1],
    [0, 0, 1, 1, 0, 0, 0, 1, 0],
    [1, 0, 1, 0, 1, 0, -1, 0, 1],
    [1, 0, -1, 0, 1, 0, 1, 0, 1],
    [1, 1, 0, 0, 0, 1, -1, 1, 0],
    [1, -1, 0, 0, 0, 1, 1, 1, 0],
    [0, 1, 1, 1, 0, 0, 0, -1, 1],
    [0, 1, -1, 1, 0, 0, 0, 1, 1]
];

function scoreSurfaceCandidate(
    collision: VoxelCollision,
    ix: number, iy: number, iz: number,
    sx: number, sy: number, sz: number,
    t1x: number, t1y: number, t1z: number,
    t2x: number, t2y: number, t2z: number
): number {
    let best = 0;
    for (let depth = 1; depth >= -1; depth--) {
        let s = 0;
        for (let da = -FLAT_R; da <= FLAT_R; da++) {
            for (let db = -FLAT_R; db <= FLAT_R; db++) {
                const px = ix + da * t1x + db * t2x - sx * depth;
                const py = iy + da * t1y + db * t2y - sy * depth;
                const pz = iz + da * t1z + db * t2z - sz * depth;
                if (collision.isVoxelSolid(px, py, pz) &&
                    !collision.isVoxelSolid(px + sx, py + sy, pz + sz)) {
                    s++;
                }
            }
        }
        if (s > best) best = s;
    }
    return best;
}

function popcount(n: number): number {
    n >>>= 0;
    n -= ((n >>> 1) & 0x55555555);
    n = (n & 0x33333333) + ((n >>> 2) & 0x33333333);
    return (((n + (n >>> 4)) & 0x0F0F0F0F) * 0x01010101) >>> 24;
}

class VoxelCollision implements Collision {
    private _gridMinX: number;
    private _gridMinY: number;
    private _gridMinZ: number;
    private _numVoxelsX: number;
    private _numVoxelsY: number;
    private _numVoxelsZ: number;
    private _voxelResolution: number;
    private _leafSize: number;
    private _treeDepth: number;
    private _nodes: Uint32Array;
    private _leafData: Uint32Array;

    private readonly _push: PushOut = { x: 0, y: 0, z: 0 };
    private readonly _normalResult = { nx: 0, ny: 0, nz: 0 };
    private readonly _constraintNormals = [
        { x: 0, y: 0, z: 0 },
        { x: 0, y: 0, z: 0 },
        { x: 0, y: 0, z: 0 }
    ];

    constructor(metadata: VoxelMetadata, nodes: Uint32Array, leafData: Uint32Array) {
        this._gridMinX = metadata.gridBounds.min[0];
        this._gridMinY = metadata.gridBounds.min[1];
        this._gridMinZ = metadata.gridBounds.min[2];
        const res = metadata.voxelResolution;
        this._numVoxelsX = Math.round((metadata.gridBounds.max[0] - metadata.gridBounds.min[0]) / res);
        this._numVoxelsY = Math.round((metadata.gridBounds.max[1] - metadata.gridBounds.min[1]) / res);
        this._numVoxelsZ = Math.round((metadata.gridBounds.max[2] - metadata.gridBounds.min[2]) / res);
        this._voxelResolution = res;
        this._leafSize = metadata.leafSize;
        this._treeDepth = metadata.treeDepth;
        this._nodes = nodes;
        this._leafData = leafData;
    }

    get voxelResolution(): number { return this._voxelResolution; }

    isFreeAt(x: number, y: number, z: number): boolean {
        if (this._nodes.length === 0) return false;
        const res = this._voxelResolution;
        const ix = Math.floor((x - this._gridMinX) / res);
        const iy = Math.floor((y - this._gridMinY) / res);
        const iz = Math.floor((z - this._gridMinZ) / res);
        if (ix < 0 || iy < 0 || iz < 0 ||
            ix >= this._numVoxelsX || iy >= this._numVoxelsY || iz >= this._numVoxelsZ) {
            return false;
        }
        return !this.isVoxelSolid(ix, iy, iz);
    }

    querySurfaceNormal(x: number, y: number, z: number,
        rdx: number, rdy: number, rdz: number): { nx: number; ny: number; nz: number } {
        const nudge = this._voxelResolution * 0.25;
        const ix = Math.floor((x + Math.sign(rdx) * nudge - this._gridMinX) / this._voxelResolution);
        const iy = Math.floor((y + Math.sign(rdy) * nudge - this._gridMinY) / this._voxelResolution);
        const iz = Math.floor((z + Math.sign(rdz) * nudge - this._gridMinZ) / this._voxelResolution);

        const result = this._normalResult;
        let bestScore = -1;
        let bestNx = 0, bestNy = 1, bestNz = 0;

        for (let c = 0; c < SURFACE_CANDIDATES.length; c++) {
            const cand = SURFACE_CANDIDATES[c];
            const dx = cand[0], dy = cand[1], dz = cand[2];
            const dot = rdx * dx + rdy * dy + rdz * dz;
            if (Math.abs(dot) < 1e-6) continue;
            const sign = dot < 0 ? 1 : -1;
            const sx = dx * sign, sy = dy * sign, sz = dz * sign;
            const score = scoreSurfaceCandidate(this, ix, iy, iz, sx, sy, sz,
                cand[3], cand[4], cand[5], cand[6], cand[7], cand[8]);
            if (score > bestScore) {
                bestScore = score;
                const mag = (Math.abs(dx) + Math.abs(dy) + Math.abs(dz)) > 1 ? INV_SQRT2 : 1;
                bestNx = sx * mag; bestNy = sy * mag; bestNz = sz * mag;
            }
        }
        result.nx = bestNx; result.ny = bestNy; result.nz = bestNz;
        return result;
    }

    queryRay(ox: number, oy: number, oz: number,
        dx: number, dy: number, dz: number, maxDist: number): RayHit | null {
        if (this._nodes.length === 0) return null;

        const res = this._voxelResolution;
        const gMinX = this._gridMinX, gMinY = this._gridMinY, gMinZ = this._gridMinZ;
        const gMaxX = gMinX + this._numVoxelsX * res;
        const gMaxY = gMinY + this._numVoxelsY * res;
        const gMaxZ = gMinZ + this._numVoxelsZ * res;
        const EPS = 1e-12;

        let tNear = 0;
        let tFar = maxDist;

        if (Math.abs(dx) > EPS) {
            let t1 = (gMinX - ox) / dx, t2 = (gMaxX - ox) / dx;
            if (t1 > t2) { const tmp = t1; t1 = t2; t2 = tmp; }
            if (t1 > tNear) tNear = t1;
            tFar = Math.min(tFar, t2);
            if (tNear > tFar) return null;
        } else if (ox < gMinX || ox >= gMaxX) return null;

        if (Math.abs(dy) > EPS) {
            let t1 = (gMinY - oy) / dy, t2 = (gMaxY - oy) / dy;
            if (t1 > t2) { const tmp = t1; t1 = t2; t2 = tmp; }
            if (t1 > tNear) tNear = t1;
            tFar = Math.min(tFar, t2);
            if (tNear > tFar) return null;
        } else if (oy < gMinY || oy >= gMaxY) return null;

        if (Math.abs(dz) > EPS) {
            let t1 = (gMinZ - oz) / dz, t2 = (gMaxZ - oz) / dz;
            if (t1 > t2) { const tmp = t1; t1 = t2; t2 = tmp; }
            if (t1 > tNear) tNear = t1;
            tFar = Math.min(tFar, t2);
            if (tNear > tFar) return null;
        } else if (oz < gMinZ || oz >= gMaxZ) return null;

        const entryX = ox + dx * tNear;
        const entryY = oy + dy * tNear;
        const entryZ = oz + dz * tNear;

        let ix = Math.max(0, Math.min(Math.floor((entryX - gMinX) / res), this._numVoxelsX - 1));
        let iy = Math.max(0, Math.min(Math.floor((entryY - gMinY) / res), this._numVoxelsY - 1));
        let iz = Math.max(0, Math.min(Math.floor((entryZ - gMinZ) / res), this._numVoxelsZ - 1));

        const stepX = dx > 0 ? 1 : (dx < 0 ? -1 : 0);
        const stepY = dy > 0 ? 1 : (dy < 0 ? -1 : 0);
        const stepZ = dz > 0 ? 1 : (dz < 0 ? -1 : 0);

        const invDx = Math.abs(dx) > EPS ? 1.0 / dx : 0;
        const invDy = Math.abs(dy) > EPS ? 1.0 / dy : 0;
        const invDz = Math.abs(dz) > EPS ? 1.0 / dz : 0;

        let tMaxX = Math.abs(dx) > EPS ? (gMinX + (ix + (dx > 0 ? 1 : 0)) * res - ox) * invDx : Infinity;
        let tMaxY = Math.abs(dy) > EPS ? (gMinY + (iy + (dy > 0 ? 1 : 0)) * res - oy) * invDy : Infinity;
        let tMaxZ = Math.abs(dz) > EPS ? (gMinZ + (iz + (dz > 0 ? 1 : 0)) * res - oz) * invDz : Infinity;

        const tDeltaX = Math.abs(dx) > EPS ? res * Math.abs(invDx) : Infinity;
        const tDeltaY = Math.abs(dy) > EPS ? res * Math.abs(invDy) : Infinity;
        const tDeltaZ = Math.abs(dz) > EPS ? res * Math.abs(invDz) : Infinity;

        let currentT = tNear;
        const maxSteps = this._numVoxelsX + this._numVoxelsY + this._numVoxelsZ;

        for (let step = 0; step < maxSteps; step++) {
            if (this.isVoxelSolid(ix, iy, iz)) {
                return { x: ox + dx * currentT, y: oy + dy * currentT, z: oz + dz * currentT };
            }
            if (tMaxX < tMaxY) {
                if (tMaxX < tMaxZ) { currentT = tMaxX; ix += stepX; tMaxX += tDeltaX; }
                else { currentT = tMaxZ; iz += stepZ; tMaxZ += tDeltaZ; }
            } else if (tMaxY < tMaxZ) {
                currentT = tMaxY; iy += stepY; tMaxY += tDeltaY;
            } else {
                currentT = tMaxZ; iz += stepZ; tMaxZ += tDeltaZ;
            }
            if (ix < 0 || iy < 0 || iz < 0 ||
                ix >= this._numVoxelsX || iy >= this._numVoxelsY || iz >= this._numVoxelsZ ||
                currentT > maxDist) {
                return null;
            }
        }
        return null;
    }

    querySphere(cx: number, cy: number, cz: number, radius: number, out: PushOut): boolean {
        if (this._nodes.length === 0) return false;
        return resolveIterative(cx, cy, cz,
            (rx, ry, rz, push) => this.resolveDeepestPenetration(rx, ry, rz, radius, push),
            this._constraintNormals, this._push, out);
    }

    queryCapsule(cx: number, cy: number, cz: number, halfHeight: number, radius: number, out: PushOut): boolean {
        if (this._nodes.length === 0) return false;
        return resolveIterative(cx, cy, cz,
            (rx, ry, rz, push) => this.resolveDeepestPenetrationCapsule(rx, ry, rz, halfHeight, radius, push),
            this._constraintNormals, this._push, out);
    }

    private resolveDeepestPenetration(cx: number, cy: number, cz: number, radius: number, out: PushOut): boolean {
        const res = this._voxelResolution;
        const gMinX = this._gridMinX, gMinY = this._gridMinY, gMinZ = this._gridMinZ;
        const radiusSq = radius * radius;

        const ixMin = Math.floor((cx - radius - gMinX) / res);
        const iyMin = Math.floor((cy - radius - gMinY) / res);
        const izMin = Math.floor((cz - radius - gMinZ) / res);
        const ixMax = Math.floor((cx + radius - gMinX) / res);
        const iyMax = Math.floor((cy + radius - gMinY) / res);
        const izMax = Math.floor((cz + radius - gMinZ) / res);

        let bestPushX = 0, bestPushY = 0, bestPushZ = 0;
        let bestPenetration = PENETRATION_EPSILON;
        let found = false;

        for (let iz = izMin; iz <= izMax; iz++) {
            for (let iy = iyMin; iy <= iyMax; iy++) {
                for (let ix = ixMin; ix <= ixMax; ix++) {
                    if (!this.isVoxelSolid(ix, iy, iz)) continue;
                    const vMinX = gMinX + ix * res, vMinY = gMinY + iy * res, vMinZ = gMinZ + iz * res;
                    const vMaxX = vMinX + res, vMaxY = vMinY + res, vMaxZ = vMinZ + res;
                    const nearX = Math.max(vMinX, Math.min(cx, vMaxX));
                    const nearY = Math.max(vMinY, Math.min(cy, vMaxY));
                    const nearZ = Math.max(vMinZ, Math.min(cz, vMaxZ));
                    const ddx = cx - nearX, ddy = cy - nearY, ddz = cz - nearZ;
                    const distSq = ddx * ddx + ddy * ddy + ddz * ddz;
                    if (distSq >= radiusSq) continue;

                    let px: number, py: number, pz: number, penetration: number;
                    if (distSq > 1e-12) {
                        const dist = Math.sqrt(distSq);
                        penetration = radius - dist;
                        const invDist = 1.0 / dist;
                        px = ddx * invDist * penetration;
                        py = ddy * invDist * penetration;
                        pz = ddz * invDist * penetration;
                    } else {
                        const distNegX = cx - vMinX, distPosX = vMaxX - cx;
                        const distNegY = cy - vMinY, distPosY = vMaxY - cy;
                        const distNegZ = cz - vMinZ, distPosZ = vMaxZ - cz;
                        const escapeX = distNegX < distPosX ? -(distNegX + radius) : (distPosX + radius);
                        const escapeY = distNegY < distPosY ? -(distNegY + radius) : (distPosY + radius);
                        const escapeZ = distNegZ < distPosZ ? -(distNegZ + radius) : (distPosZ + radius);
                        const absX = Math.abs(escapeX), absY = Math.abs(escapeY), absZ = Math.abs(escapeZ);
                        px = 0; py = 0; pz = 0;
                        if (absX <= absY && absX <= absZ) { px = escapeX; penetration = absX; }
                        else if (absY <= absZ) { py = escapeY; penetration = absY; }
                        else { pz = escapeZ; penetration = absZ; }
                    }
                    if (penetration > bestPenetration) {
                        bestPenetration = penetration;
                        bestPushX = px; bestPushY = py; bestPushZ = pz;
                        found = true;
                    }
                }
            }
        }
        if (found) { out.x = bestPushX; out.y = bestPushY; out.z = bestPushZ; }
        return found;
    }

    private resolveDeepestPenetrationCapsule(cx: number, cy: number, cz: number,
        halfHeight: number, radius: number, out: PushOut): boolean {
        const res = this._voxelResolution;
        const gMinX = this._gridMinX, gMinY = this._gridMinY, gMinZ = this._gridMinZ;
        const radiusSq = radius * radius;
        const segBottomY = cy - halfHeight;
        const segTopY = cy + halfHeight;

        const ixMin = Math.floor((cx - radius - gMinX) / res);
        const iyMin = Math.floor((segBottomY - radius - gMinY) / res);
        const izMin = Math.floor((cz - radius - gMinZ) / res);
        const ixMax = Math.floor((cx + radius - gMinX) / res);
        const iyMax = Math.floor((segTopY + radius - gMinY) / res);
        const izMax = Math.floor((cz + radius - gMinZ) / res);

        let bestPushX = 0, bestPushY = 0, bestPushZ = 0;
        let bestPenetration = PENETRATION_EPSILON;
        let found = false;

        for (let iz = izMin; iz <= izMax; iz++) {
            for (let iy = iyMin; iy <= iyMax; iy++) {
                for (let ix = ixMin; ix <= ixMax; ix++) {
                    if (!this.isVoxelSolid(ix, iy, iz)) continue;
                    const vMinX = gMinX + ix * res, vMinY = gMinY + iy * res, vMinZ = gMinZ + iz * res;
                    const vMaxX = vMinX + res, vMaxY = vMinY + res, vMaxZ = vMinZ + res;

                    let segY: number;
                    if (segTopY < vMinY) segY = segTopY;
                    else if (segBottomY > vMaxY) segY = segBottomY;
                    else { const c = (vMinY + vMaxY) * 0.5; segY = Math.max(segBottomY, Math.min(segTopY, c)); }

                    const nearX = Math.max(vMinX, Math.min(cx, vMaxX));
                    const nearY = Math.max(vMinY, Math.min(segY, vMaxY));
                    const nearZ = Math.max(vMinZ, Math.min(cz, vMaxZ));
                    const ddx = cx - nearX, ddy = segY - nearY, ddz = cz - nearZ;
                    const distSq = ddx * ddx + ddy * ddy + ddz * ddz;
                    if (distSq >= radiusSq) continue;

                    let px: number, py: number, pz: number, penetration: number;
                    if (distSq > 1e-12) {
                        const dist = Math.sqrt(distSq);
                        penetration = radius - dist;
                        const invDist = 1.0 / dist;
                        px = ddx * invDist * penetration;
                        py = ddy * invDist * penetration;
                        pz = ddz * invDist * penetration;
                    } else {
                        const distNegX = cx - vMinX, distPosX = vMaxX - cx;
                        const distNegY = segY - vMinY, distPosY = vMaxY - segY;
                        const distNegZ = cz - vMinZ, distPosZ = vMaxZ - cz;
                        const escapeX = distNegX < distPosX ? -(distNegX + radius) : (distPosX + radius);
                        const escapeY = distNegY < distPosY ? -(distNegY + radius) : (distPosY + radius);
                        const escapeZ = distNegZ < distPosZ ? -(distNegZ + radius) : (distPosZ + radius);
                        const absX = Math.abs(escapeX), absY = Math.abs(escapeY), absZ = Math.abs(escapeZ);
                        px = 0; py = 0; pz = 0;
                        if (absX <= absY && absX <= absZ) { px = escapeX; penetration = absX; }
                        else if (absY <= absZ) { py = escapeY; penetration = absY; }
                        else { pz = escapeZ; penetration = absZ; }
                    }
                    if (penetration > bestPenetration) {
                        bestPenetration = penetration;
                        bestPushX = px; bestPushY = py; bestPushZ = pz;
                        found = true;
                    }
                }
            }
        }
        if (found) { out.x = bestPushX; out.y = bestPushY; out.z = bestPushZ; }
        return found;
    }

    isVoxelSolid(ix: number, iy: number, iz: number): boolean {
        if (this._nodes.length === 0 ||
            ix < 0 || iy < 0 || iz < 0 ||
            ix >= this._numVoxelsX || iy >= this._numVoxelsY || iz >= this._numVoxelsZ) {
            return false;
        }
        const { _leafSize: leafSize, _treeDepth: treeDepth } = this;
        const blockX = Math.floor(ix / leafSize);
        const blockY = Math.floor(iy / leafSize);
        const blockZ = Math.floor(iz / leafSize);
        let nodeIndex = 0;

        for (let level = treeDepth - 1; level >= 0; level--) {
            const node = this._nodes[nodeIndex] >>> 0;
            if (node === SOLID_LEAF_MARKER) return true;
            const childMask = (node >>> 24) & 0xFF;
            if (childMask === 0) return this.checkLeafByIndex(node, ix, iy, iz);
            const bitX = (blockX >>> level) & 1;
            const bitY = (blockY >>> level) & 1;
            const bitZ = (blockZ >>> level) & 1;
            const octant = (bitZ << 2) | (bitY << 1) | bitX;
            if ((childMask & (1 << octant)) === 0) return false;
            const baseOffset = node & 0x00FFFFFF;
            const prefix = (1 << octant) - 1;
            const childOffset = popcount(childMask & prefix);
            nodeIndex = baseOffset + childOffset;
        }
        const node = this._nodes[nodeIndex] >>> 0;
        if (node === SOLID_LEAF_MARKER) return true;
        return this.checkLeafByIndex(node, ix, iy, iz);
    }

    private checkLeafByIndex(node: number, ix: number, iy: number, iz: number): boolean {
        const leafDataIndex = node & 0x00FFFFFF;
        const vx = ix & 3, vy = iy & 3, vz = iz & 3;
        const bitIndex = vz * 16 + vy * 4 + vx;
        if (bitIndex < 32) {
            const lo = this._leafData[leafDataIndex * 2] >>> 0;
            return ((lo >>> bitIndex) & 1) === 1;
        }
        const hi = this._leafData[leafDataIndex * 2 + 1] >>> 0;
        return ((hi >>> (bitIndex - 32)) & 1) === 1;
    }
}

/** Legacy v1.0 adapter that negates X/Y on inputs and outputs. */
class FlippedVoxelCollision extends VoxelCollision {
    querySurfaceNormal(x: number, y: number, z: number,
        rdx: number, rdy: number, rdz: number): { nx: number; ny: number; nz: number } {
        const result = super.querySurfaceNormal(-x, -y, z, -rdx, -rdy, rdz);
        result.nx = -result.nx;
        result.ny = -result.ny;
        return result;
    }
    queryRay(ox: number, oy: number, oz: number,
        dx: number, dy: number, dz: number, maxDist: number): RayHit | null {
        const hit = super.queryRay(-ox, -oy, oz, -dx, -dy, dz, maxDist);
        if (hit) { hit.x = -hit.x; hit.y = -hit.y; }
        return hit;
    }
    querySphere(cx: number, cy: number, cz: number, radius: number, out: PushOut): boolean {
        const result = super.querySphere(-cx, -cy, cz, radius, out);
        if (result) { out.x = -out.x; out.y = -out.y; }
        return result;
    }
    queryCapsule(cx: number, cy: number, cz: number, halfHeight: number, radius: number, out: PushOut): boolean {
        const result = super.queryCapsule(-cx, -cy, cz, halfHeight, radius, out);
        if (result) { out.x = -out.x; out.y = -out.y; }
        return result;
    }
    isFreeAt(x: number, y: number, z: number): boolean {
        return super.isFreeAt(-x, -y, z);
    }
}

const loadVoxelCollision = async (jsonUrl: string): Promise<VoxelCollision> => {
    const metaResponse = await fetch(jsonUrl);
    if (!metaResponse.ok) {
        throw new Error(`Failed to fetch voxel metadata: ${metaResponse.statusText}`);
    }
    const metadata: VoxelMetadata = await metaResponse.json();

    const binUrl = jsonUrl.replace('.voxel.json', '.voxel.bin');
    const binResponse = await fetch(binUrl);
    if (!binResponse.ok) {
        throw new Error(`Failed to fetch voxel binary: ${binResponse.statusText}`);
    }
    const buffer = await binResponse.arrayBuffer();
    const view = new Uint32Array(buffer);

    const nodes = view.slice(0, metadata.nodeCount);
    const leafData = view.slice(metadata.nodeCount, metadata.nodeCount + metadata.leafDataCount);

    const isLegacy = !metadata.version || parseFloat(metadata.version) < 1.1;
    return isLegacy
        ? new FlippedVoxelCollision(metadata, nodes, leafData)
        : new VoxelCollision(metadata, nodes, leafData);
};

export { VoxelCollision, loadVoxelCollision };
